import { useEffect, useState } from "react";
import { Box, styled } from "@mui/system";
import { TablePagination, Tooltip } from "@mui/material";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import FoodBankIcon from "@mui/icons-material/FoodBank";
import styles from "./RestaurantList.module.css";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  useLoaderContext,
  useToastContext,
} from "../../../../Context/ToastContext";
import { useUserContext } from "../../../../Context/UserContext";
import { Helmet } from "react-helmet";
import { apiGet, apiPut } from "../../../../utils/ApiManager";

const searchAllRestaurants = process.env.REACT_APP_FETCH_SEARCH_ALL_RESTAURANTS;
const putHandleDeactivate = process.env.REACT_APP_PUT_HANDLE_DEACTIVATE;
const putHandleActivate = process.env.REACT_APP_PUT_HANDLE_ACTIVATE;

const RestaurantList = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(100);
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigateTo = useNavigate();
  const { showToast } = useToastContext();
  const { requestConfirmation, getToken } = useUserContext();
  const { setLoading } = useLoaderContext();

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(1);
  };

  const handleView = (restaurantId) => {
    navigateTo(`/restaurants/${restaurantId}`);
  };

  const handleEdit = (restaurantId) => {
    navigateTo(`/restaurants/${restaurantId}/update`);
  };

  const handleRegister = (restaurantId) => {
    navigateTo(`/restaurants/register`);
  };

  const handleFetchData = async () => {
    try {
      setLoading(true);
      let params = {
        pageSize: rowsPerPage,
        pageNo: page,
      };
      if (searchQuery) {
        params.searchText = searchQuery;
      }
      const queryString = new URLSearchParams(params).toString();
      const response = await apiGet(`${searchAllRestaurants}?${queryString}`);
      if (response.success) {
        setData(response?.data || []);
        setTotalCount(response.totalCount || 0);
      } else {
        showToast(
          response.message || "Error fetcing restaurant list.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Error fetcing restaurant list.",
        "error"
      );
    } finally {
      setLoading(false);
    } 
  };

  const handleDeactivate = async (restaurantId) => {
    try {
      const result = await requestConfirmation(
        "Want to deactivate restaurant ?"
      );
      if (!result) return;
      setLoading(true);
      const response = await apiPut(putHandleDeactivate, { restaurantId });
      if (response.success) {
        showToast(
          response?.message || "Restaurant deactivated successfully.",
          "success"
        );
        handleFetchData();
      } else {
        showToast(
          response.message || "Error deactivating restaurant.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Error deactivating restaurant.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (restaurantId) => {
    try {
      const result = await requestConfirmation(
        "Want to re-activate this restaurant ?"
      );
      if (!result) return;

      const response = await apiPut(putHandleActivate, { restaurantId });
      if (response.success) {
        showToast(
          response.data?.message || "Restaurant activated successfully.",
          "success"
        );
        handleFetchData();
      } else {
        showToast(response.message || "Error activating restaurant.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Error activating restaurant.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewMenu = async (restaurantId) => {
    try {
      navigateTo(`/restaurants/${restaurantId}/menu`);
    } catch (error) {
      showToast("Error viewing menu.", "error");
    }
  };

  useEffect(() => {
    if (searchQuery) {
      const getData = setTimeout(() => {
        handleFetchData();
      }, 1000);
      return () => clearTimeout(getData);
    } else {
      handleFetchData();
    }
  }, [page, rowsPerPage, searchQuery]);

  return (
    <Box className={styles.mainContainer}>
      <Helmet>
        <title>Restaurants | DiNEST</title>
        <meta name="description" content="View restaurants on DiNEST" />
      </Helmet>
      <Box className={styles.restaurantListContainer}>
        <Typography variant="h4" className={styles.containerTitle}>
          Restaurants
        </Typography>

        <Button
          onClick={handleRegister}
          className={styles.registerButton}
          variant="contained"
        >
          Register
        </Button>

        {/* Search Bar */}
        {data?.length > 0 || searchQuery ? (
          <>
            <div className={styles.searchBarContainer}>
              <TextField
                label="Search restaurant by name"
                variant="outlined"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchBar}
              />
            </div>

            <TableContainer className={styles.tableContainer} component={Paper}>
              <Table stickyHeader aria-label="restaurant table">
                <TableHead className={styles.tableHeading}>
                  <TableRow>
                    <TableCell className={styles.tableHead}>Sr. No</TableCell>
                    <TableCell className={styles.tableHead}>
                      Restaurant ID
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Restaurant Name
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Restaurant Number
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Restaurant Email
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Owner Name
                    </TableCell>
                    <TableCell className={styles.tableHead}>Status</TableCell>
                    <TableCell className={styles.tableHead}>
                      Activate/Deactivate
                    </TableCell>
                    <TableCell className={styles.tableHead}>View</TableCell>
                    <TableCell className={styles.tableHead}>Edit</TableCell>
                    <TableCell className={styles.tableHead}>Menu</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data &&
                    data.map((restaurant, index) => (
                      <TableRow
                        className={styles.tableDataContainer}
                        key={restaurant.restaurantId}
                      >
                        <TableCell className={styles.tableData}>
                          {rowsPerPage * (page - 1) + index + 1}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {restaurant?.restaurantId || "N/A"}
                        </TableCell>
                        <TableCell
                          className={styles.tableData}
                          sx={{ maxWidth: 150 }}
                        >
                          <Tooltip
                            title={restaurant?.restaurantName || "N/A"}
                            arrow
                          >
                            <Typography
                              style={{
                                display: "inline-block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 200,
                              }}
                            >
                              {" "}
                              {restaurant?.restaurantName || "N/A"}
                            </Typography>
                          </Tooltip>
                        </TableCell>{" "}
                        <TableCell className={styles.tableData}>
                          {restaurant?.restaurantNumber || "N/A"}
                        </TableCell>
                        <TableCell
                          className={styles.tableData}
                          sx={{ maxWidth: 150 }}
                        >
                          <Tooltip
                            title={restaurant?.restaurantEmail || "N/A"}
                            arrow
                          >
                            <Typography
                              style={{
                                display: "inline-block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 100,
                              }}
                            >
                              {" "}
                              {restaurant?.restaurantEmail || "N/A"}
                            </Typography>
                          </Tooltip>
                        </TableCell>{" "}
                        <TableCell className={styles.tableData}>
                          <Tooltip
                            title={
                              restaurant?.restaurantOwner
                                ? `${
                                    restaurant?.restaurantOwner?.firstName || ""
                                  } ${
                                    restaurant?.restaurantOwner?.lastName || ""
                                  }`
                                : "N/A"
                            }
                            arrow
                          >
                            <span
                              style={{
                                display: "inline-block",
                                maxWidth: 100,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {restaurant?.restaurantOwner
                                ? `${
                                    restaurant?.restaurantOwner?.firstName ||
                                    "N/A"
                                  } ${
                                    restaurant?.restaurantOwner?.lastName || ""
                                  }`
                                : "N/A"}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          <StatusLabel active={restaurant?.isActive}>
                            {restaurant?.isActive ? "Active" : "Inactive"}
                          </StatusLabel>
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          <Button
                            variant="contained"
                            sx={{ textTransform: "initial" }}
                            color={restaurant?.isActive ? "error" : "success"}
                            onClick={() => {
                              restaurant?.isActive
                                ? handleDeactivate(restaurant?.restaurantId)
                                : handleActivate(restaurant?.restaurantId);
                            }}
                          >
                            {restaurant?.isActive ? "Deactivate" : "Activate"}
                          </Button>
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          <IconButton
                            onClick={() => handleView(restaurant?.restaurantId)}
                            sx={{ color: "#207d46" }}
                          >
                            <Tooltip title="View Restaurant" arrow>
                              <VisibilityIcon />
                            </Tooltip>
                          </IconButton>
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          <IconButton
                            onClick={() => handleEdit(restaurant?.restaurantId)}
                            sx={{ color: "#207d46" }}
                          >
                            <Tooltip title="Edit Restaurant" arrow>
                              <ModeEditOutlineIcon />
                            </Tooltip>
                          </IconButton>
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          <IconButton
                            onClick={() =>
                              handleViewMenu(restaurant?.restaurantId)
                            }
                            sx={{ color: "#207d46" }}
                          >
                            <Tooltip title="Restaurant Menu" arrow>
                              <FoodBankIcon />
                            </Tooltip>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <div className={styles.paginationContainer}>
              <TablePagination
                className={`${styles.pagination}`}
                rowsPerPageOptions={[2, 5, 10, 15]}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  "& .MuiTablePagination-actions button": {
                    backgroundColor: "transparent",
                    color: "white !important",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "&:focus": {
                      outline: "3px solid #2196F3 !important",
                    },
                  },
                  "& .MuiTablePagination-actions button svg": {
                    fill: "white !important",
                  },
                  "& .MuiSelect-root": {
                    color: "white !important",
                  },
                  "& .MuiSelect-icon": {
                    fill: "white !important",
                  },
                }}
                slotProps={{
                  select: {
                    "aria-label": "Rows per page",
                  },
                  actions: {
                    showFirstButton: true,
                    showLastButton: true,
                    slots: {
                      firstPageIcon: FirstPageRoundedIcon,
                      lastPageIcon: LastPageRoundedIcon,
                      nextPageIcon: ChevronRightRoundedIcon,
                      backPageIcon: ChevronLeftRoundedIcon,
                    },
                  },
                }}
              />
            </div>
          </>
        ) : (
          <h2 style={{ color: "red", textAlign: "center", marginTop: "2rem" }}>
            No restaurants registered yet !
          </h2>
        )}
      </Box>
    </Box>
  );
};

const StatusLabel = styled("span")(({ active }) => ({
  color: active ? "#4CAF50" : "#f44336",
  fontWeight: "bold",
  textTransform: "uppercase",
}));

export default RestaurantList;
